import {
  formatAssessmentDateTime,
  getDeadlineDate,
  toValidDate,
} from 'app/RefleksiPotensiPage/utils'

import type {
  RefleksiPotensiSoalResponse,
  SoalRefleksiPotensi,
} from 'types/refleksi-potensi'

const STORAGE_VERSION = 'v1'
const START_WARNING_KEY = `refleksi-potensi:start-warning:${STORAGE_VERSION}`
const EXPIRED_SESSION_KEY = `refleksi-potensi:expired-session:${STORAGE_VERSION}`
export const REFLEKSI_POTENSI_ANSWER_DRAFT_KEY = `refleksi-potensi:answer-draft:${STORAGE_VERSION}`

export type QuestionPosition = {
  methodIndex: number
  questionIndex: number
}

export type RefleksiPotensiAnswerDraft = {
  idSesiPengerjaan: number
  waktuMulaiPengerjaan: string
  idSoal: number
  idOpsiJawaban: number
}

export type AssessmentDeadline = {
  startedAt: Date
  deadline: Date
  startedAtLabel: string
  deadlineLabel: string
}

const isPositiveInteger = (value: number) =>
  Number.isInteger(value) && value > 0

export const isValidQuestionSession = (
  data?: RefleksiPotensiSoalResponse
): data is RefleksiPotensiSoalResponse => {
  if (
    !data ||
    !isPositiveInteger(data.idSesiPengerjaan) ||
    !toValidDate(data.waktuMulaiPengerjaan) ||
    !Array.isArray(data.metode) ||
    data.metode.length === 0
  ) {
    return false
  }

  return data.metode.every(
    (method) =>
      Boolean(method?.nama?.trim()) &&
      Boolean(method?.deskripsi?.trim()) &&
      Boolean(method?.estimasiWaktuPengerjaan?.trim()) &&
      Array.isArray(method.soal) &&
      method.soal.length > 0 &&
      method.soal.every((question) => {
        if (
          !isPositiveInteger(question?.id) ||
          !question?.studiKasus?.trim() ||
          !question?.pertanyaanPemantik?.trim() ||
          !Array.isArray(question.opsiJawaban) ||
          question.opsiJawaban.length === 0 ||
          !question.opsiJawaban.every(
            (option) =>
              isPositiveInteger(option?.id) &&
              Boolean(option?.deskripsi?.trim())
          )
        ) {
          return false
        }

        const selectedAnswerId = question.idOpsiJawabanDipilih

        return (
          selectedAnswerId === null ||
          selectedAnswerId === undefined ||
          question.opsiJawaban.some((option) => option.id === selectedAnswerId)
        )
      })
  )
}

export const getInitialAnswers = (data: RefleksiPotensiSoalResponse) => {
  const answers: Record<number, number> = {}

  data.metode.forEach((method) => {
    method.soal.forEach((question) => {
      const selectedAnswerId = question.idOpsiJawabanDipilih

      if (typeof selectedAnswerId === 'number') {
        answers[question.id] = selectedAnswerId
      }
    })
  })

  return answers
}

export const getFirstUnansweredPosition = (
  data: RefleksiPotensiSoalResponse
): QuestionPosition | null => {
  for (
    let methodIndex = 0;
    methodIndex < data.metode.length;
    methodIndex += 1
  ) {
    const method = data.metode[methodIndex]

    for (
      let questionIndex = 0;
      questionIndex < method.soal.length;
      questionIndex += 1
    ) {
      const selectedAnswerId = method.soal[questionIndex].idOpsiJawabanDipilih

      if (selectedAnswerId === null || selectedAnswerId === undefined) {
        return { methodIndex, questionIndex }
      }
    }
  }

  return null
}

export const getAssessmentDeadline = (
  startedAtValue?: string | null
): AssessmentDeadline | null => {
  const startedAt = toValidDate(startedAtValue)
  const deadline = getDeadlineDate(startedAtValue)
  const startedAtLabel = formatAssessmentDateTime(startedAtValue)
  const deadlineLabel = formatAssessmentDateTime(deadline?.toISOString())

  if (!startedAt || !deadline || !startedAtLabel || !deadlineLabel) {
    return null
  }

  return {
    startedAt,
    deadline,
    startedAtLabel,
    deadlineLabel,
  }
}

export const getQuestionActionLabel = (
  position: QuestionPosition,
  data: RefleksiPotensiSoalResponse
) => {
  const method = data.metode[position.methodIndex]
  const isLastQuestion = position.questionIndex === method.soal.length - 1

  if (isLastQuestion) return `Selesaikan Bagian ${position.methodIndex + 1}`

  return 'Selanjutnya'
}

export const getOptionLetter = (index: number) => {
  let value = index + 1
  let label = ''

  while (value > 0) {
    value -= 1
    label = String.fromCharCode(65 + (value % 26)) + label
    value = Math.floor(value / 26)
  }

  return label
}

const readStorage = (key: string) => {
  if (typeof window === 'undefined') return null

  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStorage = (key: string, value: string) => {
  if (typeof window === 'undefined') return false

  try {
    window.localStorage.setItem(key, value)
    return true
  } catch {
    // Storage may be unavailable in private browsing or a restricted WebView.
    return false
  }
}

const removeStorage = (key: string) => {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(key)
  } catch {
    // Storage may be unavailable in private browsing or a restricted WebView.
  }
}

export const hasAcknowledgedStartWarning = (sessionId: number) =>
  readStorage(START_WARNING_KEY) === String(sessionId)

export const acknowledgeStartWarning = (sessionId: number) =>
  writeStorage(START_WARNING_KEY, String(sessionId))

export const hasAcknowledgedExpiredSession = (startedAt: string | null) =>
  Boolean(startedAt) && readStorage(EXPIRED_SESSION_KEY) === startedAt

export const acknowledgeExpiredSession = (startedAt: string) =>
  writeStorage(EXPIRED_SESSION_KEY, startedAt)

export const clearAcknowledgedExpiredSession = () =>
  removeStorage(EXPIRED_SESSION_KEY)

const isAnswerDraft = (value: unknown): value is RefleksiPotensiAnswerDraft => {
  if (!value || typeof value !== 'object') return false

  const draft = value as RefleksiPotensiAnswerDraft

  return (
    isPositiveInteger(draft.idSesiPengerjaan) &&
    Boolean(toValidDate(draft.waktuMulaiPengerjaan)) &&
    isPositiveInteger(draft.idSoal) &&
    isPositiveInteger(draft.idOpsiJawaban)
  )
}

export const clearRefleksiPotensiAnswerDraft = () =>
  removeStorage(REFLEKSI_POTENSI_ANSWER_DRAFT_KEY)

export const saveRefleksiPotensiAnswerDraft = (
  draft: RefleksiPotensiAnswerDraft
) => {
  const isSaved = writeStorage(
    REFLEKSI_POTENSI_ANSWER_DRAFT_KEY,
    JSON.stringify(draft)
  )

  if (!isSaved) clearRefleksiPotensiAnswerDraft()

  return isSaved
}

export const getRefleksiPotensiAnswerDraft = (
  data: RefleksiPotensiSoalResponse,
  position: QuestionPosition
): RefleksiPotensiAnswerDraft | null => {
  const storedDraft = readStorage(REFLEKSI_POTENSI_ANSWER_DRAFT_KEY)
  if (!storedDraft) return null

  let draft: unknown

  try {
    draft = JSON.parse(storedDraft)
  } catch {
    clearRefleksiPotensiAnswerDraft()
    return null
  }

  if (!isAnswerDraft(draft)) {
    clearRefleksiPotensiAnswerDraft()
    return null
  }

  const answerDraft = draft
  const question = getQuestionAtPosition(data, position)
  const deadline = getAssessmentDeadline(data.waktuMulaiPengerjaan)
  const isMatchingSession =
    answerDraft.idSesiPengerjaan === data.idSesiPengerjaan &&
    answerDraft.waktuMulaiPengerjaan === data.waktuMulaiPengerjaan
  const isMatchingQuestion =
    isMatchingSession &&
    answerDraft.idSoal === question.id &&
    question.opsiJawaban.some(
      (option) => option.id === answerDraft.idOpsiJawaban
    )
  const isActive = Boolean(deadline && Date.now() < deadline.deadline.getTime())

  if (!isMatchingQuestion || !isActive) {
    clearRefleksiPotensiAnswerDraft()
    return null
  }

  return answerDraft
}

export const getQuestionAtPosition = (
  data: RefleksiPotensiSoalResponse,
  position: QuestionPosition
): SoalRefleksiPotensi =>
  data.metode[position.methodIndex].soal[position.questionIndex]
